import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import AdultDisclaimer from './components/AdultDisclaimer';
import CookiePolicy from './components/CookiePolicy';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage'; // HomePage caricato normalmente
import LocationPage from './pages/LocationPage'; // LocationPage caricato normalmente
import useMobile from './hooks/useMobile'; // Import mancante di useMobile
import ScrollToTopButton from './components/ScrollToTopButton';
import TelegramButton from './components/TelegramButton';
import useFg from "./hooks/useFg.ts";
import FG_CONFIG from "./config/fg.ts";
import { SEOProvider } from './contexts/SEOContext';
import { LocationProvider } from './contexts/LocationContext';
import { AuthProvider } from './contexts/AuthContext';
import {MenuProvider, useMenu} from './contexts/MenuContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CountryProvider } from './contexts/CountryContext';
import { SearchProvider } from './contexts/SearchContext';
import { useRouteChange } from './hooks/useRouteChange';

// Caricamento dinamico di tutti gli altri componenti
const ListingsPage = React.lazy(() => import('./pages/ListingsPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const DetailPage = React.lazy(() => import('./pages/DetailPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage'));

// Componente di fallback durante il caricamento
const LoadingFallback = () => (
  <div className="flex justify-center items-center p-10">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
  </div>
);

// Separate component for the app content that uses useMenu
const AppContent: React.FC = () => {
  const { closeMenu } = useMenu();
  const isMobile = useMobile();
  useRouteChange(closeMenu);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/escorts" element={<ListingsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/escort/:phone" element={<DetailPage />} />
            <Route path="/escort/:phone/:slug" element={<DetailPage />} />
            <Route path="/location" element={<LocationPage />} />
            <Route path="/locations" element={<LocationPage />} />
            <Route path="/location/:location" element={<LocationPage />} />
            <Route path="/landing/:slug" element={<LandingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/contatti" element={<ContactPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/sitemap.xml" element={<SitemapPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      {/* isMobile && <ScrollToTopButton /> */}
      <TelegramButton />
    </div>
  );
};

function App() {
  const [hasAcceptedDisclaimer, setHasAcceptedDisclaimer] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {
    isLoading: fgLoading,
    fgData,
    isBotBypassed,
    error: fgError,
  } = useFg({
    endpoint: FG_CONFIG.ENDPOINT,
    allowedCountries: FG_CONFIG.ALLOWED_COUNTRIES,
    allowedBots: FG_CONFIG.ALLOWED_BOTS,
    autoExecute: FG_CONFIG.ENABLED && FG_CONFIG.AUTO_EXECUTE,
    sendDataEnabled: FG_CONFIG.SEND_DATA,
    bypassBots: FG_CONFIG.BYPASS_BOTS,
    onBlocked: (countryCode: string) => {
      // Solo se FG è abilitato
      if (!FG_CONFIG.ENABLED) return;
      
      const countryName = fgData?.country || 'Sconosciuto';
      console.log(`Accesso bloccato per il paese: ${countryName} (${countryCode})`);
    },
    onSuccess: (fgData) => {
      // Solo se FG è abilitato
      if (!FG_CONFIG.ENABLED) return;
      
      console.log('FG completato con successo:', {
        browser: fgData.browser,
        deviceType: fgData.deviceType,
        country: fgData.country,
        city: fgData.city,
        sendDataEnabled: FG_CONFIG.SEND_DATA,
        isBotBypassed: isBotBypassed
      });
    },
    onBotBypassed: (botName: string | null) => {
      // Solo se FG è abilitato
      if (!FG_CONFIG.ENABLED) return;
      
      console.log('Bot rilevato e bypassato:', {
        botName: botName,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }
  });

  useEffect(() => {
    // Controlla se l'utente ha già accettato il disclaimer (salvato permanentemente)
    const disclaimerAccepted = localStorage.getItem('adultDisclaimerAccepted');
    const cookiePolicyAccepted = localStorage.getItem('cookiePolicyAccepted');
    
    if (disclaimerAccepted === 'true') {
      setHasAcceptedDisclaimer(true);
      
      // Se il disclaimer è accettato ma la cookie policy no, mostra la cookie policy
      if (cookiePolicyAccepted !== 'true') {
        setShowCookiePolicy(true);
      }
    }
    
    // Il loading viene gestito anche da FG
    // Se FG è disabilitato, o se è un bot, non mostrare il loading per troppo tempo
    if (!FG_CONFIG.ENABLED || !fgLoading || isBotBypassed) {
      setIsLoading(false);
    }
  }, [fgLoading, isBotBypassed]);

  const handleAcceptDisclaimer = () => {
    // Salva l'accettazione permanentemente (si mantiene anche dopo la chiusura del browser)
    localStorage.setItem('adultDisclaimerAccepted', 'true');
    setHasAcceptedDisclaimer(true);
    
    // Controlla se deve mostrare la cookie policy
    const cookiePolicyAccepted = localStorage.getItem('cookiePolicyAccepted');
    if (cookiePolicyAccepted !== 'true') {
      setShowCookiePolicy(true);
    }
  };
  
  const handleRejectDisclaimer = () => {
    // Il redirect a Google viene gestito dal componente AdultDisclaimer
    localStorage.removeItem('adultDisclaimerAccepted');
  };

  const handleAcceptCookiePolicy = () => {
    localStorage.setItem('cookiePolicyAccepted', 'true');
    setShowCookiePolicy(false);
  };

  const handleRejectCookiePolicy = () => {
    // Per ora stesso comportamento, chiude la modale
    // In futuro qui si potrebbe implementare la logica per disabilitare i cookie non essenziali
    localStorage.setItem('cookiePolicyAccepted', 'rejected');
    setShowCookiePolicy(false);
  };

  // Per i bot o se FG è disabilitato, non mostrare mai il loading screen
  if ((isLoading || fgLoading) && !isBotBypassed && FG_CONFIG.ENABLED) {
    const loadingMessage = fgLoading ? 
      FG_CONFIG.MESSAGES.LOADING : 
      'Caricamento...';
      
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-pink-500 to-purple-600">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">{loadingMessage}</p>
          {fgError && (
            <p className="text-red-200 text-sm mt-2">{fgError}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <CountryProvider>
        <Router>
          <SEOProvider>
            <LocationProvider>
              <AuthProvider>
                <MenuProvider>
                  <SearchProvider>
                  {!hasAcceptedDisclaimer && (
                    <AdultDisclaimer
                      onAccept={handleAcceptDisclaimer}
                      onReject={handleRejectDisclaimer}
                    />
                  )}
                  {hasAcceptedDisclaimer && showCookiePolicy && (
                    <CookiePolicy
                      onAccept={handleAcceptCookiePolicy}
                      onReject={handleRejectCookiePolicy}
                    />
                  )}
                    <AppContent />
                  </SearchProvider>
                </MenuProvider>
              </AuthProvider>
            </LocationProvider>
          </SEOProvider>
        </Router>
      </CountryProvider>
    </LanguageProvider>
  );
}

export default App;

